import { useState, useEffect } from "react";

import { Typography, TextField } from "@material-ui/core";
import SmarthopConfirmDialog from "@smarthop/form/SmarthopConfirmDialog";

function LoadSearchContactConfirmModalView({
	open,
	contactType, // "email" or "phone"
	contactValue,
	loadInfo,
	onClose,
	onConfirm,
}) {
	const [contact, setContact] = useState("");
	const [error, setError] = useState("");
	const [minAllowed, setMinAllowed] = useState("");
	const [goal, setGoal] = useState("");
	const [minAllowedError, setMinAllowedError] = useState("");
	const [goalError, setGoalError] = useState("");

	useEffect(() => {
		if (open) {
			// Only pre-populate contact if it matches the type
			let initialContact = "";
			if (contactValue) {
				if (contactType === "email") {
					// Only populate if it looks like an email
					const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
					if (emailRegex.test(contactValue)) {
						initialContact = contactValue;
					}
				} else if (contactType === "phone") {
					// Only populate if it looks like a phone (no @ symbol)
					if (!contactValue.includes("@")) {
						initialContact = contactValue;
					}
				}
			}
			setContact(initialContact);
			setError("");
			// Round Est price to the next 50 dollars
			const defaultMinAllowed =
				loadInfo?.price != null && loadInfo?.price !== ""
					? loadInfo.price
					: loadInfo?.est_price != null
					? Math.ceil(loadInfo.est_price / 50) * 50
					: "";
			// Round the goal to 0 decimal places
			const defaultGoal =
				loadInfo?.max_price != null &&
				defaultMinAllowed != null &&
				Number(loadInfo.max_price) > Number(defaultMinAllowed)
					? // Round the goal to the next 100 dollars
					  Math.ceil((Number(defaultMinAllowed) + (Number(loadInfo.max_price) - Number(defaultMinAllowed)) / 2) / 50) *
					  50
					: Math.ceil(defaultMinAllowed / 50) * 50;
			setMinAllowed(defaultMinAllowed !== "" ? String(defaultMinAllowed) : "");
			setGoal(defaultGoal !== "" ? String(defaultGoal) : "");
			setMinAllowedError("");
			setGoalError("");
		}
	}, [open, contactValue, loadInfo, contactType]);

	const validateContact = (value) => {
		if (!value || value.trim() === "") {
			return "Contact information is required";
		}

		if (contactType === "email") {
			// Basic email validation
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(value)) {
				return "Please enter a valid email address";
			}
		} else if (contactType === "phone") {
			// Basic phone validation (allows various formats)
			const phoneRegex = /^[0-9\s\-\+\(\)ext\.x]+$/i;
			if (!phoneRegex.test(value)) {
				return "Please enter a valid phone number";
			}
		}

		return "";
	};

	const handleContactChange = (e) => {
		const value = e.target.value;
		setContact(value);
		const validationError = validateContact(value);
		setError(validationError);
	};

	const handleConfirm = () => {
		const validationError = validateContact(contact);
		if (validationError) {
			setError(validationError);
			return;
		}
		const parsedMinAllowed = parseFloat(minAllowed);
		const parsedGoal = parseFloat(goal);
		if (Number.isNaN(parsedMinAllowed)) {
			setMinAllowedError("Please enter a valid number");
			return;
		}
		if (Number.isNaN(parsedGoal)) {
			setGoalError("Please enter a valid number");
			return;
		}
		onConfirm?.({
			contact,
			minAllowed: parsedMinAllowed,
			goal: parsedGoal,
		});
	};

	const title = contactType === "email" ? "Confirm Email Contact" : "Confirm Phone Contact";
	const message =
		contactType === "email"
			? "An email will be sent to this broker about the load:"
			: "A voice agent will call this broker about the load:";

	const isValid =
		!error &&
		!minAllowedError &&
		!goalError &&
		contact &&
		contact.trim() !== "" &&
		minAllowed !== "" &&
		goal !== "" &&
		!Number.isNaN(parseFloat(minAllowed)) &&
		!Number.isNaN(parseFloat(goal));

	return (
		<SmarthopConfirmDialog
			open={!!open}
			title={title}
			message={message}
			handleClose={onClose}
			handleAccept={handleConfirm}
			mode={"NORMAL"}
			acceptMsg={contactType === "email" ? "Send Email" : "Initiate Call"}
			disableAccept={!isValid}
		>
			<div className="flex flex-col gap-12 pt-8">
				{/* Load Information */}
				<div className="flex flex-col gap-4 pb-8 border-b-1">
					<Typography className="text-12 text-gray-600">
						<strong>Load:</strong> {loadInfo?.load_id || loadInfo?.tripid || "N/A"}
					</Typography>
					<Typography className="text-12 text-gray-600">
						<strong>Route:</strong> {loadInfo?.origin || "N/A"} → {loadInfo?.destination || "N/A"}
					</Typography>
					<Typography className="text-12 text-gray-600">
						<strong>Broker:</strong> {loadInfo?.clientid || "N/A"}
					</Typography>
				</div>

				{/* Contact Input */}
				<div className="flex flex-col gap-4">
					<Typography className="text-13 font-semibold">
						{contactType === "email" ? "Email Address:" : "Phone Number:"}
					</Typography>
					<TextField
						fullWidth
						variant="outlined"
						size="small"
						value={contact}
						onChange={handleContactChange}
						error={!!error}
						helperText={error}
						placeholder={contactType === "email" ? "broker@example.com" : "1234567890 ext 123"}
						autoFocus
						InputProps={{
							className: "text-14",
						}}
					/>
				</div>

				{/* Additional Info */}
				<div className="flex flex-col pt-4">
					<Typography className="text-11 text-orange italic">
						{contactType === "email"
							? "*Note: The broker will receive an email with your carrier information and load details"
							: "*Note: An AI voice agent will initiate a call to discuss this load"}
					</Typography>
				</div>
				<div className="flex flex-col gap-4 pt-8">
					<Typography className="text-13 font-semibold">Min Allowed Rate</Typography>
					<TextField
						fullWidth
						variant="outlined"
						size="small"
						value={minAllowed}
						onChange={(e) => {
							const value = e.target.value;
							setMinAllowed(value);
							if (value === "" || Number.isNaN(Number(value))) {
								setMinAllowedError("Please enter a valid number");
							} else {
								setMinAllowedError("");
							}
						}}
						error={!!minAllowedError}
						helperText={minAllowedError}
						placeholder="0"
						InputProps={{
							className: "text-14",
						}}
					/>
				</div>
				<div className="flex flex-col gap-4">
					<Typography className="text-13 font-semibold">Goal Rate</Typography>
					<TextField
						fullWidth
						variant="outlined"
						size="small"
						value={goal}
						onChange={(e) => {
							const value = e.target.value;
							setGoal(value);
							if (value === "" || Number.isNaN(Number(value))) {
								setGoalError("Please enter a valid number");
							} else {
								setGoalError("");
							}
						}}
						error={!!goalError}
						helperText={goalError}
						placeholder="0"
						InputProps={{
							className: "text-14",
						}}
					/>
				</div>
			</div>
		</SmarthopConfirmDialog>
	);
}

export default LoadSearchContactConfirmModalView;
